import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";

interface Category {
  id: number;
  name: string;
}

const fuelTypeSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t("fuelTypes.form.validation.nameRequired")),
    category: z
      .number()
      .min(1, t("fuelTypes.form.validation.categoryRequired")),
    unit: z.string().optional(),
  });

type FuelTypeFormValues = z.infer<typeof fuelTypeSchema>;

interface FuelTypeFormProps {
  id?: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const FuelTypeForm = ({
  id,
  onSuccess,
  onCancel,
}: FuelTypeFormProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // Fetch categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery<
    Category[]
  >({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("categories/"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      return data;
    },
  });

  // Ensure categories is always an array from the results
  const categories = Array.isArray(categoriesData?.results)
    ? categoriesData.results
    : [];

  const form = useForm<FuelTypeFormValues>({
    resolver: zodResolver(fuelTypeSchema(t)),
    defaultValues: {
      name: "",
    },
  });

  // Fetch fuel type data if in edit mode
  const { data: fuelTypeData, isSuccess: isFuelTypeLoaded } = useQuery({
    queryKey: ["fuel-type", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`fuel-types/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(t("fuelTypes.error.fetchSingle"));
      }
      const data = await response.json();
      return data;
    },
    enabled: isEdit && !!id,
  });

  // Reset form when both categories and fuel type data are loaded
  useEffect(() => {
    if (isEdit && isFuelTypeLoaded && !isLoadingCategories && fuelTypeData) {
      // Extract category ID whether it's an object or just an ID
      const categoryId =
        typeof fuelTypeData.category === "object"
          ? fuelTypeData.category.id
          : fuelTypeData.category;

      form.reset({
        ...fuelTypeData,
        category: categoryId,
      });
    }
  }, [isEdit, isFuelTypeLoaded, isLoadingCategories, fuelTypeData, form]);

  const mutation = useMutation({
    mutationFn: async (data: FuelTypeFormValues) => {
      const url = isEdit ? `fuel-types/${id}/` : "fuel-types/";
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t("fuelTypes.error.save"));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fuel-types"] });
      onSuccess();
    },
  });

  const onSubmit = async (data: FuelTypeFormValues) => {
    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("fuelTypes.form.name")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fuelTypes.form.namePlaceholder")}
                  {...field}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("fuelTypes.form.category")}</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(Number(value));
                }}
                value={field.value ? field.value.toString() : ""}
                disabled={mutation.isPending || isLoadingCategories}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("fuelTypes.form.selectCategory")}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.id}
                      value={category.id.toString()}
                    >
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="unit"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("fuelTypes.form.unit")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("fuelTypes.form.unitPlaceholder")}
                  {...field}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t("common.saving")
              : isEdit
                ? t("common.update")
                : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
